import Backbone from 'backbone';
import $ from 'jquery';
import {ReactElement} from 'react';
import ReactDOM from 'react-dom/client';

import AssemblyWizardContainerView from 'assembly/WizardContainer';
import * as benchlingUtil from 'BenchlingUtil';
import BuffersView from 'buffers/BuffersView';
import * as contextmenu from 'components/ContextMenu';
import FileBarView from 'components/FileBarView';
import * as statusbar from 'components/StatusBar';
import StatusBarView from 'components/StatusBarView';
import {SidepanelSection} from 'constants/NavConstants';
import FileStates from 'FileStates';
import navigateToURL from 'lib/navigateToURL';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import SearchDNAView from 'molbio/biological-search/SearchDNAView';
import {initFullScreen} from 'nav/full-screen/FullScreen';
import {initHelpContainer} from 'nav/HelpContainer';
import {initNavBar} from 'nav/navbar/NavBar';
import * as ScheduledModal from 'nav/scheduled-modal';
import {Sidepanel} from 'nav/sidepanel/Sidepanel';
import {SubworkspaceHeaderView} from 'nav/workspace/header/SubworkspaceHeaderView';
import {initWorkspaceContainer} from 'nav/workspace/WorkspaceContainer';
import notifications from 'notifications';
import {unmountRootsAsync} from 'redux-stores/ReduxDOMRender';
import ToolbarView from 'toolbar/ToolbarView';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as net from 'util/Net';
import {BasicEvent} from 'util/Types';

// No model set for view, so use placeholder.
class Editor extends Backbone.View<Backbone.Model> {
  queryKey!: {[key: string]: string};
  sequences!: FileStates;
  contextmenu!: typeof contextmenu.view;
  fileBar!: FileBarView;
  toolbar!: ToolbarView;
  statusBar!: StatusBarView;
  assemblyWizardContainer!: AssemblyWizardContainerView;
  buffersView!: BuffersView;
  searchDNAView!: SearchDNAView;
  subworkspaceHeaderView!: SubworkspaceHeaderView;
  premature!: boolean;
  fullScreenReactRoot: ReactDOM.Root | null = null;
  helpContainerReactRoot: ReactDOM.Root | null = null;
  navBarReactRoot: ReactDOM.Root | null = null;
  sidepanelReactRoot: ReactDOM.Root | null = null;
  workspaceContainerReactRoot: ReactDOM.Root | null = null;

  static initClass(): void {
    this.prototype.el = 'body';

    this.prototype.events = {
      // @ts-expect-error: does not match vendored types.
      'click .js-home-logo': 'navigateHome',
    };
  }

  navigateHome(e: JQueryEventObject): void {
    e.preventDefault();
    navigateToURL('/editor');
  }

  renderReact(component: ReactElement<unknown> | null, sidebarSection?: SidepanelSection): void {
    this.fileBar.renderReact(component, sidebarSection);
  }

  override initialize(): void {
    // This needs to be stored before location is set as files are activated.
    ({queryKey: this.queryKey} = benchlingUtil.parseUri(window.location.href));

    this.sequences = FileStates.get();

    this.contextmenu = contextmenu.view;
    this.toolbar = new ToolbarView();
    this.statusBar = new StatusBarView();
    this.assemblyWizardContainer = new AssemblyWizardContainerView();
    this.buffersView = new BuffersView();
    this.searchDNAView = new SearchDNAView({model: SearchDNAData.get()});
    this.subworkspaceHeaderView = new SubworkspaceHeaderView();
    this.fileBar = new FileBarView({
      // @ts-expect-error
      toolbar: this.toolbar,
      buffersView: this.buffersView,
      statusBar: this.statusBar,
      searchDNA: this.searchDNAView,
      assemblyWizardContainer: this.assemblyWizardContainer,
      subworkspaceHeaderView: this.subworkspaceHeaderView,
    });
    this.workspaceContainerReactRoot = initWorkspaceContainer(this.workspaceContainerReactRoot);
    this.navBarReactRoot = initNavBar(this.navBarReactRoot);
    this.sidepanelReactRoot = Sidepanel.init(this.sidepanelReactRoot);
    this.fullScreenReactRoot = initFullScreen(this.fullScreenReactRoot);
    this.helpContainerReactRoot = initHelpContainer(this.helpContainerReactRoot);
    ScheduledModal.init();
    this.showSync();
  }

  closeSubcomponents(): void {
    // Used in Editor Routes as a pre-render call to close components that may have been left open
    this.searchDNAView.closeSearch();
  }

  showSync(): void {
    this.premature = false;
    this.listenTo(Backbone, 'long-sync', function handleLongSync(this: Editor, longXHRCount: number): void {
      this.$('#reconnecting').show();
    });
    this.listenTo(
      Backbone,
      'good-sync',
      function handleGoodSync(this: Editor, xhr: JQueryXHR, xhrCount: number, longXHRCount: number): void {
        statusbar.hideOverlayMessage();
        if (xhrCount === 0) {
          this.$('#reconnecting').hide();
          statusbar.setTimeoutMessage('Saved.');
          if (this.premature) {
            notifications.error('Connectivity Error: Please retry your last action.');
            this.premature = false;
          }
        } else if (longXHRCount > 0) {
          this.$('#reconnecting').show();
        } else if (xhrCount > 1) {
          statusbar.showOverlayMessage(`Saving... (${xhrCount})`);
        } else {
          statusbar.showOverlayMessage('Saving...');
        }
      }
    );
    this.listenTo(Backbone, 'syncing', (xhr: JQueryXHR, xhrCount: number, longXHRCount: number): void => {
      if (xhrCount > 1) {
        statusbar.showOverlayMessage(`Saving... (${xhrCount})`);
      } else {
        statusbar.showOverlayMessage('Saving...');
      }
    });
    this.listenTo(Backbone, 'premature-sync', function handlePrematureSync(this: Editor) {
      this.premature = true;
      this.$('#reconnecting').show();
    });
    this.listenTo(
      Backbone,
      'fail-sync',
      function handleFailSync(this: Editor, xhr: JQueryXHR, xhrCount: number, longXHRCount: number): void {
        if (longXHRCount > 0) {
          this.$('#reconnecting').show();
        } else {
          this.$('#reconnecting').hide();
        }
        statusbar.setTimeoutMessage('Error while saving.');
        this.premature = false;
      }
    );

    // In integration tests that have to load the editor and then another page, the alert shows
    // and blocks the test from continuing.
    if (window.ENABLE_EDITOR_PENDING_REQUEST_CHECK) {
      $(window).on('beforeunload.editor', () => {
        window.beforeUnloadType = 'editor';
        // net.xhrCounter ref-counts Ajax requests and Backbone ref-counts syncs.
        // The first includes non-Backbone Ajax requests and the second includes non-Ajax syncs
        // (e.g., debounced ones that haven't fired yet), so it's necessary to check both.
        if (net.xhrCounter.getCount() > 0 || Backbone.getXHRCount() > 0) {
          return (
            'There is currently a request to the server pending. ' +
            'You may lose recent changes by navigating away.'
          );
        } else {
          // Don't show a message.
          return undefined;
        }
      });
    }
  }

  override remove(): Backbone.View<Backbone.Model> {
    unmountRootsAsync(
      [
        this.fullScreenReactRoot,
        this.helpContainerReactRoot,
        this.navBarReactRoot,
        this.sidepanelReactRoot,
        this.workspaceContainerReactRoot,
      ],
      () => {
        this.fullScreenReactRoot = null;
        this.helpContainerReactRoot = null;
        this.navBarReactRoot = null;
        this.sidepanelReactRoot = null;
        this.workspaceContainerReactRoot = null;

        $(window).off('.editor');
        super.remove();
      }
    );
    // @ts-expect-error: `render` should return `this`
    return this;
  }

  // @ts-expect-error: should return `this`
  render(): void {
    this.fileBar.render();
    this.$el.append(this.contextmenu.render().el);
    this.statusBar.assemblySwitcher.renderItems();
    this.$('.js-userMenu-templateCollections').click((e: BasicEvent): void => {
      benchlingUtil.navigate(e);
    });

    LoadingScreen.init();
  }
}
Editor.initClass();

export default Editor;
